import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Search, Loader2, Sparkles, MapPin, Building2, TrendingUp, Users, Globe2, RefreshCw,
    ShieldCheck, Star, ChevronRight, ExternalLink, X, ArrowLeft, Upload, Copy, RotateCcw,
    Linkedin, Mail, Phone, Bookmark, BookmarkPlus, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Avatar } from "@/components/Avatar";
import CandidateDrawer from "./CandidateDrawer";
import BulkInviteModal from "./BulkInviteModal";
import PresetCopyModal from "./PresetCopyModal";

export default function RecruitCandidateSearch() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const searchId = params.get("search") || "";

    const [searches, setSearches] = useState([]);
    const [brief, setBrief] = useState(null);
    const [candidates, setCandidates] = useState({ internal: [], external: [] });
    const [rediscovery, setRediscovery] = useState(null);
    const [tab, setTab] = useState("all"); // all | simonara | apollo
    const [phase, setPhase] = useState("idle"); // idle | loading | matching | ready
    const [selected, setSelected] = useState(null); // candidate being viewed in drawer
    const [booleanMode, setBooleanMode] = useState(false);
    const [booleanQuery, setBooleanQuery] = useState("");
    const [showBoolHelp, setShowBoolHelp] = useState(false);
    const [presets, setPresets] = useState([]);
    const [showPresetSave, setShowPresetSave] = useState(false);
    const [newPresetName, setNewPresetName] = useState("");
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [copyPreset, setCopyPreset] = useState(null); // {id, name, query} being copied
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    // External channel filter — only affects the Apollo (external) list.
    // Applied ON TOP of the tab selector so admins can prune to only
    // reachable candidates before opening the drawer. any | linkedin | email | phone
    const [externalChannelFilter, setExternalChannelFilter] = useState("any");
    // Sourcing Saved Views — one-click presets that snapshot tab/channel/sort/
    // boolean state. Owned per admin, stored server-side so they persist across
    // devices.
    const [savedViews, setSavedViews] = useState([]);
    const [showSaveView, setShowSaveView] = useState(false);
    const [saveViewName, setSaveViewName] = useState("");
    const loadSavedViews = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/recruit/saved-views");
            setSavedViews(data.results || []);
        } catch { /* silent — no views yet */ }
    }, []);
    useEffect(() => { loadSavedViews(); }, [loadSavedViews]);
    const [sortMode, setSortMode] = useState("match"); // match | verified_first | trust_first

    // Load list of searches for the picker
    useEffect(() => {
        api.get("/admin/recruit/searches?limit=50")
            .then((r) => setSearches(r.data.items || []))
            .catch((err) => toast.error(formatApiError(err)));
    }, []);

    const loadSearch = useCallback(async (id) => {
        setPhase("loading");
        try {
            const [{ data: b }, { data: cands }, { data: rd }] = await Promise.all([
                api.get(`/admin/recruit/searches/${id}`),
                api.get(`/admin/recruit/searches/${id}/candidates?limit=200`),
                api.get(`/admin/recruit/searches/${id}/rediscovery`),
            ]);
            setBrief(b);
            const internal = (cands.items || []).filter((c) => c.source === "simonara");
            const external = (cands.items || []).filter((c) => c.source === "apollo");
            setCandidates({ internal, external });
            setRediscovery(rd);
            setPhase("ready");
        } catch (err) {
            toast.error(formatApiError(err));
            setPhase("idle");
        }
    }, []);

    useEffect(() => { if (searchId) loadSearch(searchId); }, [searchId, loadSearch]);

    // Poll for candidates who just restored their passport while the
    // recruiter's tab is open. Every 60s we ask
    // /admin/recruit/searches/{id}/restored-recently?since=<lastPolledAt>.
    // If any come back, toast + refetch results so the "Restored just now"
    // pill actually lands in front of the recruiter instead of relying on
    // them refreshing the page. Server clock is source of truth — we send
    // back the `checked_at` it returned on the previous poll to avoid
    // client-clock drift.
    useEffect(() => {
        if (!searchId || phase !== "ready") return undefined;
        let alive = true;
        let since = new Date().toISOString();
        const seen = new Set();  // dedupe cross-poll — no repeat toasts for the same restore
        const tick = async () => {
            if (!alive) return;
            try {
                const { data } = await api.get(
                    `/admin/recruit/searches/${searchId}/restored-recently?since=${encodeURIComponent(since)}`,
                );
                if (!alive) return;
                since = data.checked_at || since;
                const fresh = (data.items || []).filter(
                    (c) => c.candidate_id && !seen.has(c.candidate_id),
                );
                if (fresh.length > 0) {
                    fresh.forEach((c) => seen.add(c.candidate_id));
                    if (fresh.length === 1) {
                        const c = fresh[0];
                        toast.success(
                            `${c.name || c.email || "A talent"} just restored their passport — they're back in your search.`,
                            { duration: 6000 },
                        );
                    } else {
                        toast.success(
                            `${fresh.length} talents just restored their passports — they're back in your search.`,
                            { duration: 6000 },
                        );
                    }
                    // Refetch results so the amber "Restored just now" pill
                    // actually renders on the affected cards.
                    loadSearch(searchId);
                }
            } catch (err) {
                // Non-fatal — recruiter still sees stale results, just no toast.
                console.warn("[recruit-restore-poll] tick failed:", err?.message || err);
            }
        };
        const iv = setInterval(tick, 60 * 1000);
        return () => { alive = false; clearInterval(iv); };
    }, [searchId, phase, loadSearch]);

    // Load saved boolean presets for this search.
    const loadPresets = useCallback(async (id) => {
        if (!id) return;
        try {
            const { data } = await api.get(`/admin/recruit/searches/${id}/boolean-presets`);
            setPresets(data.items || []);
        } catch (err) {
            /* Non-blocking — presets are optional */
            console.warn("boolean presets load failed", err);
        }
    }, []);
    useEffect(() => { loadPresets(searchId); }, [searchId, loadPresets]);

    const applyPreset = (preset) => {
        setBooleanMode(true);
        setBooleanQuery(preset.query);
        toast.success(`Loaded preset '${preset.name}'.`);
    };
    const savePreset = async () => {
        const name = newPresetName.trim();
        if (!name || !booleanQuery.trim()) return;
        try {
            const { data } = await api.post(`/admin/recruit/searches/${searchId}/boolean-presets`, { name, query: booleanQuery.trim() });
            setPresets((p) => [data, ...p]);
            toast.success(`Preset '${name}' saved.`);
            setShowPresetSave(false);
            setNewPresetName("");
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const deletePreset = async (preset) => {
        if (!window.confirm(`Delete preset '${preset.name}'?`)) return;
        try {
            await api.delete(`/admin/recruit/searches/${searchId}/boolean-presets/${preset.id}`);
            setPresets((p) => p.filter((x) => x.id !== preset.id));
            toast.success("Preset deleted.");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const runMatch = async () => {
        if (!searchId) return;
        setPhase("matching");
        try {
            const body = { external_limit: 25, internal_limit: 50 };
            if (booleanMode && booleanQuery.trim()) body.boolean_query = booleanQuery.trim();
            const { data } = await api.post(`/admin/recruit/searches/${searchId}/find-candidates`, body, { timeout: 90_000 });
            setCandidates({ internal: data.internal || [], external: data.external || [] });
            toast.success(`Found ${data.internal_count} internal + ${data.external_count} external matches.`);
            setPhase("ready");
        } catch (err) {
            toast.error(formatApiError(err));
            setPhase("ready");
        }
    };

    // Search picker view
    if (!searchId) {
        return (
            <div className="space-y-4" data-testid="recruit-search-picker">
                <div className="card-soft p-5">
                    <h2 className="font-heading font-medium text-lg mb-1">Pick a search to run</h2>
                    <p className="text-sm text-muted-foreground">Each candidate search runs against a saved AI Hiring Brief™. Create one in Job Parser first.</p>
                </div>
                {searches.length === 0 ? (
                    <div className="card-soft p-10 text-center">
                        <Sparkles size={20} className="mx-auto text-violet-500" />
                        <p className="text-sm text-muted-foreground mt-2">No saved searches yet.</p>
                        <Button onClick={() => navigate("/admin/recruit/job-parser")} className="mt-4" data-testid="picker-go-parser">Open Job Parser</Button>
                    </div>
                ) : (
                    <div className="card-soft p-2 divide-y divide-border">
                        {searches.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => setParams({ search: s.id })}
                                data-testid={`picker-search-${s.id}`}
                                className="w-full py-3 px-3 flex items-center justify-between text-left hover:bg-muted/30 rounded transition"
                            >
                                <div className="min-w-0">
                                    <div className="font-medium truncate">{s.brief?.job_title || s.source_ref}</div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {s.brief?.location || "—"} · {s.brief?.remote_policy || "—"} · {(s.brief?.required_skills || []).length} required skills
                                    </div>
                                </div>
                                <ChevronRight size={14} className="text-muted-foreground shrink-0 ml-3" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    const b = brief?.brief || {};
    // Client-side "Verified only" filter — quick UX without a server round-trip.
    // Apollo rows can never be id_verified so verifiedOnly implicitly narrows
    // to the Simonara tab. We DO keep the tab pill counts un-filtered so the
    // recruiter can see how many verified vs total exist at a glance.
    const applyVerified = (list) => verifiedOnly ? list.filter((c) => c.id_verified) : list;
    const applyChannelFilter = (list) => {
        if (externalChannelFilter === "any") return list;
        return list.filter((c) => {
            if (c.source !== "apollo") return true;  // never hide internal rows
            if (externalChannelFilter === "linkedin") return !!c.linkedin_url;
            if (externalChannelFilter === "email")    return !!(c.email || c.email_available);
            if (externalChannelFilter === "phone")    return !!c.phone;
            return true;
        });
    };

    // ----- Sourcing Saved Views handlers -----
    const applySavedView = (v) => {
        setTab(v.tab || "all");
        setExternalChannelFilter(v.channel || "any");
        setVerifiedOnly(!!v.verified_only);
        setBooleanMode(!!v.boolean_mode);
        setBooleanQuery(v.boolean_query || "");
        setSortMode(v.sort_mode || "match");
        toast.success(`View "${v.name}" applied.`);
    };
    const saveCurrentAsView = async () => {
        const name = (saveViewName || "").trim();
        if (!name) {
            toast.error("Give this view a short name first.");
            return;
        }
        try {
            const { data } = await api.post("/admin/recruit/saved-views", {
                name,
                search_id: searchId || null,
                tab,
                channel: externalChannelFilter,
                verified_only: verifiedOnly,
                boolean_mode: booleanMode,
                boolean_query: booleanQuery,
                sort_mode: sortMode,
            });
            setSavedViews((prev) => {
                const other = prev.filter((v) => v.id !== data.id && v.name !== data.name);
                return [data, ...other];
            });
            setShowSaveView(false);
            setSaveViewName("");
            toast.success(`Saved "${data.name}".`);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const deleteSavedView = async (v) => {
        try {
            await api.delete(`/admin/recruit/saved-views/${v.id}`);
            setSavedViews((prev) => prev.filter((x) => x.id !== v.id));
            toast.success(`Removed "${v.name}".`);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    // Channel-availability counts on the raw external list — always visible
    // beside the chip label so the admin knows how much a filter will trim.
    const channelCounts = {
        linkedin: candidates.external.filter((c) => c.linkedin_url).length,
        email:    candidates.external.filter((c) => c.email || c.email_available).length,
        phone:    candidates.external.filter((c) => c.phone).length,
    };
    // Sort comparators — verified_first pushes id_verified=true to the top and
    // then falls back to match score; trust_first is the trust-then-match sort.
    const sortCmp = (a, b) => {
        if (sortMode === "verified_first") {
            const dv = (b.id_verified ? 1 : 0) - (a.id_verified ? 1 : 0);
            if (dv !== 0) return dv;
        } else if (sortMode === "trust_first") {
            const dt = (b.trust_score || 0) - (a.trust_score || 0);
            if (dt !== 0) return dt;
        }
        return (b.match_score || 0) - (a.match_score || 0);
    };
    const currentList = tab === "simonara" ? applyVerified([...candidates.internal]).sort(sortCmp)
        : tab === "apollo" ? applyChannelFilter([...candidates.external]).sort(sortCmp)
        : applyChannelFilter(applyVerified([...candidates.internal, ...candidates.external])).sort(sortCmp);
    const verifiedCount = candidates.internal.filter((c) => c.id_verified).length;

    return (
        <div data-testid="recruit-candidate-search" className="space-y-4">
            {/* Brief header */}
            <div className="card-soft p-4 flex items-start gap-3 flex-wrap">
                <Button variant="ghost" size="sm" onClick={() => setParams({})} data-testid="csrch-back-to-picker">
                    <ArrowLeft size={13} className="mr-1" /> Change search
                </Button>
                <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-violet-600 uppercase tracking-wider font-semibold">AI Hiring Brief™</div>
                    <div className="font-heading font-medium text-lg truncate">{b.job_title || "…"}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-2 mt-0.5">
                        {b.seniority && <span>{b.seniority}</span>}
                        {b.location && <><span>·</span><span><MapPin size={10} className="inline -mt-0.5" /> {b.location}</span></>}
                        {b.remote_policy && <><span>·</span><span>{b.remote_policy}</span></>}
                    </div>
                </div>
                <Button variant="outline" onClick={() => setShowBulkModal(true)} data-testid="csrch-bulk-invite-btn">
                    <Upload size={13} className="mr-2" /> Bulk invite
                </Button>
                <Button onClick={runMatch} disabled={phase === "matching"} data-testid="csrch-run-match">
                    {phase === "matching" ? <Loader2 size={13} className="animate-spin mr-2" /> : <RefreshCw size={13} className="mr-2" />}
                    {phase === "matching" ? "Matching…" : "Re-run match"}
                </Button>
            </div>

            {/* Rediscovery panel */}
            {rediscovery && (
                <div className="card-soft p-4 bg-gradient-to-br from-violet-500/5 to-fuchsia-500/5 border-violet-500/20" data-testid="rediscovery-panel">
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-violet-500/15 text-violet-600 flex items-center justify-center shrink-0"><Sparkles size={16} /></div>
                        <div className="min-w-0 flex-1">
                            <div className="font-heading font-medium text-sm">
                                You already know <span className="text-violet-600 font-bold">{rediscovery.total}</span> professionals who may fit this role.
                            </div>
                            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                                {rediscovery.buckets.map((buk) => (
                                    <div key={buk.key} className="bg-background/60 border border-violet-500/10 rounded-lg p-2" data-testid={`rediscovery-${buk.key}`}>
                                        <div className="font-heading font-bold mono-score text-lg text-violet-600">{buk.count}</div>
                                        <div className="text-[11px] font-medium">{buk.label}</div>
                                        <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{buk.hint}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Boolean Search (Phase 5) */}
            <div className="card-soft p-3" data-testid="csrch-boolean-bar">
                <div className="flex items-start gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-2 shrink-0 cursor-pointer" data-testid="csrch-boolean-toggle-label">
                        <input
                            type="checkbox"
                            checked={booleanMode}
                            onChange={(e) => setBooleanMode(e.target.checked)}
                            className="accent-violet-500"
                            data-testid="csrch-boolean-toggle"
                        />
                        <span className="text-sm font-medium">Boolean filter</span>
                    </label>
                    <input
                        type="text"
                        value={booleanQuery}
                        onChange={(e) => setBooleanQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && booleanMode && booleanQuery.trim()) runMatch(); }}
                        placeholder={'e.g. "barista" AND (SF OR Oakland) NOT nights'}
                        disabled={!booleanMode}
                        className="flex-1 min-w-[240px] px-2.5 py-1.5 text-sm bg-background border border-border rounded-md focus:border-violet-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed font-mono"
                        data-testid="csrch-boolean-input"
                    />
                    {/* Saved-filter presets (per-search) */}
                    {booleanMode && presets.length > 0 && (
                        <select
                            value=""
                            onChange={(e) => {
                                const p = presets.find((x) => x.id === e.target.value);
                                if (p) applyPreset(p);
                            }}
                            className="px-2 py-1.5 text-xs bg-background border border-border rounded-md hover:border-violet-500 focus:outline-none focus:border-violet-500 shrink-0"
                            data-testid="csrch-preset-select"
                        >
                            <option value="">Load preset…</option>
                            {presets.map((p) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    )}
                    {booleanMode && booleanQuery.trim() && (
                        <button
                            type="button"
                            onClick={() => setShowPresetSave((v) => !v)}
                            className="text-xs px-2 py-1.5 rounded-md border border-border hover:border-violet-500 hover:text-violet-600 shrink-0"
                            data-testid="csrch-preset-save-btn"
                        >
                            Save as…
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowBoolHelp((v) => !v)}
                        className="text-xs text-violet-600 hover:underline shrink-0 self-center"
                        data-testid="csrch-boolean-help"
                    >
                        Syntax help
                    </button>
                </div>
                {showPresetSave && (
                    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="csrch-preset-save-form">
                        <input
                            type="text"
                            autoFocus
                            value={newPresetName}
                            onChange={(e) => setNewPresetName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") savePreset(); }}
                            placeholder="Preset name (e.g. 'Bay Area seniors')"
                            className="flex-1 min-w-[220px] px-2.5 py-1.5 text-xs bg-background border border-border rounded-md focus:border-violet-500 focus:outline-none"
                            maxLength={60}
                            data-testid="csrch-preset-name-input"
                        />
                        <Button size="sm" onClick={savePreset} disabled={!newPresetName.trim()} data-testid="csrch-preset-save-confirm">Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setShowPresetSave(false); setNewPresetName(""); }} data-testid="csrch-preset-save-cancel">Cancel</Button>
                    </div>
                )}
                {showBoolHelp && (
                    <div className="mt-2 text-xs bg-muted/40 rounded p-2 space-y-1 font-mono" data-testid="csrch-boolean-help-panel">
                        <div><b>AND / OR / NOT</b> (case-insensitive) — combine terms.</div>
                        <div><b>&quot;quoted phrases&quot;</b> — match multi-word terms exactly.</div>
                        <div><b>( )</b> — group with parentheses. Example:</div>
                        <div className="text-violet-600 pl-4">(python OR go) AND (senior OR staff) NOT contractor</div>
                        <div className="text-muted-foreground text-[11px]">Filter is applied on top of the AI Match™ requirement breakdown — only candidates matching both are returned.</div>
                    </div>
                )}
                {/* Preset management list */}
                {booleanMode && presets.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="csrch-preset-chips">
                        <span className="text-[11px] text-muted-foreground">Presets:</span>
                        {presets.map((p) => (
                            <span key={p.id} className="inline-flex items-center gap-1 text-[11px] bg-muted/60 rounded px-1.5 py-0.5" data-testid={`csrch-preset-chip-${p.id}`}>
                                <button onClick={() => applyPreset(p)} className="hover:text-violet-600 font-medium" title={`Load ${p.name}`} data-testid={`csrch-preset-apply-${p.id}`}>{p.name}</button>
                                <button onClick={() => setCopyPreset(p)} className="text-muted-foreground hover:text-violet-600 p-0.5" title="Copy to another search" aria-label={`Copy ${p.name} to another search`} data-testid={`csrch-preset-copy-${p.id}`}>
                                    <Copy size={11} />
                                </button>
                                <button onClick={() => deletePreset(p)} className="text-muted-foreground hover:text-red-500 p-0.5" title="Delete preset" aria-label={`Delete ${p.name}`} data-testid={`csrch-preset-delete-${p.id}`}>
                                    <X size={11} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Sourcing Saved Views — one-click presets combining tab +
                external channel + boolean + verified + sort. Personal to
                each admin so recruiters can build their own shortlists
                (e.g. "US remote engineers on LinkedIn only"). */}
            <div className="card-soft p-3 flex items-center gap-2 flex-wrap" data-testid="csrch-saved-views-row">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mr-1">
                    <Bookmark size={11} /> Saved views
                </div>
                {savedViews.length === 0 && (
                    <span className="text-xs text-muted-foreground italic">
                        No saved views yet. Save the current filters to reuse them with one click.
                    </span>
                )}
                {savedViews.map((v) => (
                    <span
                        key={v.id}
                        className="inline-flex items-center gap-1 text-xs rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-700 pl-3 pr-1 py-1"
                        data-testid={`csrch-saved-view-${v.id}`}
                    >
                        <button
                            onClick={() => applySavedView(v)}
                            className="hover:underline"
                            data-testid={`csrch-saved-view-apply-${v.id}`}
                            title={`${v.tab} · ${v.channel} · ${v.verified_only ? "verified only" : "any trust"} · ${v.sort_mode}${v.boolean_query ? ` · boolean:${v.boolean_query.slice(0, 40)}` : ""}`}
                        >
                            {v.name}
                        </button>
                        <button
                            onClick={() => deleteSavedView(v)}
                            className="text-muted-foreground hover:text-red-500 p-0.5"
                            title="Delete saved view"
                            aria-label={`Delete ${v.name}`}
                            data-testid={`csrch-saved-view-delete-${v.id}`}
                        >
                            <X size={11} />
                        </button>
                    </span>
                ))}
                {!showSaveView ? (
                    <button
                        onClick={() => setShowSaveView(true)}
                        className="ml-auto text-xs px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 inline-flex items-center gap-1.5 transition"
                        data-testid="csrch-saved-view-open"
                    >
                        <BookmarkPlus size={11} /> Save current view
                    </button>
                ) : (
                    <div className="ml-auto flex items-center gap-1.5" data-testid="csrch-saved-view-form">
                        <input
                            autoFocus
                            value={saveViewName}
                            onChange={(e) => setSaveViewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveCurrentAsView(); if (e.key === "Escape") { setShowSaveView(false); setSaveViewName(""); } }}
                            placeholder="Name this view…"
                            className="h-8 text-xs px-3 rounded-full border border-border bg-background focus:outline-none focus:border-violet-500 w-56"
                            data-testid="csrch-saved-view-name-input"
                        />
                        <button
                            onClick={saveCurrentAsView}
                            className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 inline-flex items-center gap-1.5"
                            data-testid="csrch-saved-view-save"
                        >
                            Save
                        </button>
                        <button
                            onClick={() => { setShowSaveView(false); setSaveViewName(""); }}
                            className="text-xs px-2 py-1.5 rounded-full text-muted-foreground hover:text-foreground"
                            data-testid="csrch-saved-view-cancel"
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>


            {/* Tabs + match run */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex bg-muted/40 rounded-lg p-1 border border-border">
                    {[
                        { key: "all", label: "All", n: candidates.internal.length + candidates.external.length, Icon: Users },
                        { key: "simonara", label: "Simonara Passports", n: candidates.internal.length, Icon: ShieldCheck },
                        { key: "apollo", label: "Apollo", n: candidates.external.length, Icon: Globe2 },
                    ].map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`csrch-tab-${t.key}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${tab === t.key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            <t.Icon size={13} /> {t.label} <span className="text-[11px] text-muted-foreground">({t.n})</span>
                        </button>
                    ))}
                </div>
                <button
                    onClick={() => setVerifiedOnly((v) => !v)}
                    data-testid="csrch-verified-only-toggle"
                    disabled={tab === "apollo"}
                    title={tab === "apollo" ? "Verified filter applies to Simonara Passports only" : "Show only ID-verified candidates"}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition ${
                        verifiedOnly
                            ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
                            : "bg-background text-muted-foreground border-border hover:text-emerald-600 hover:border-emerald-500/40"
                    } ${tab === "apollo" ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                    <ShieldCheck size={13} />
                    Verified only
                    <span className="text-[11px] opacity-75">({verifiedCount})</span>
                </button>
                <select
                    value={sortMode}
                    onChange={(e) => setSortMode(e.target.value)}
                    data-testid="csrch-sort-select"
                    className="px-2.5 py-1.5 text-sm bg-background border border-border rounded-md hover:border-violet-500 focus:outline-none focus:border-violet-500 cursor-pointer"
                    aria-label="Sort candidates"
                >
                    <option value="match">Sort: Best match</option>
                    <option value="verified_first">Sort: Verified first</option>
                    <option value="trust_first">Sort: Most verified</option>
                </select>
            </div>

            {/* External sourcing channel filter — only shown when there are
                Apollo results to prune. Lets the admin surface only rows
                with a reachable channel (LinkedIn / email / phone) before
                opening the drawer. Doesn't touch Simonara internal rows. */}
            {(tab === "all" || tab === "apollo") && candidates.external.length > 0 && (
                <div
                    className="card-soft p-3 flex items-center gap-2 flex-wrap"
                    data-testid="csrch-channel-filter-row"
                >
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mr-1">
                        External channels
                    </span>
                    {[
                        { key: "any",      label: "Any",       n: candidates.external.length, Icon: Globe2 },
                        { key: "linkedin", label: "LinkedIn",  n: channelCounts.linkedin,     Icon: Linkedin },
                        { key: "email",    label: "Email",     n: channelCounts.email,        Icon: Mail },
                        { key: "phone",    label: "Phone",     n: channelCounts.phone,        Icon: Phone },
                    ].map((c) => (
                        <button
                            key={c.key}
                            onClick={() => setExternalChannelFilter(c.key)}
                            data-testid={`csrch-channel-chip-${c.key}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 ${
                                externalChannelFilter === c.key
                                    ? "border-violet-500/60 bg-violet-500/10 text-violet-700 font-medium"
                                    : "border-border hover:border-violet-500/40 hover:bg-muted/30"
                            }`}
                        >
                            <c.Icon size={11} /> {c.label}
                            <span className="text-[10px] opacity-70">({c.n})</span>
                        </button>
                    ))}
                    {externalChannelFilter !== "any" && (
                        <span className="text-[11px] text-muted-foreground ml-auto italic">
                            Simonara Passport rows are always visible.
                        </span>
                    )}
                </div>
            )}

            {/* Candidate list */}
            {phase === "loading" ? (
                <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-violet-500" /></div>
            ) : currentList.length === 0 ? (
                <div className="card-soft p-10 text-center" data-testid="csrch-empty">
                    <Search size={22} className="mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mt-2">No candidates yet — click <b>Re-run match</b> to search.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="csrch-candidate-grid">
                    {currentList.map((c) => (
                        <CandidateCard key={`${c.source}-${c.candidate_id}`} c={c} onOpen={() => setSelected(c)} />
                    ))}
                </div>
            )}

            {selected && (
                <CandidateDrawer
                    candidate={selected}
                    searchId={searchId}
                    onClose={() => setSelected(null)}
                />
            )}
            {showBulkModal && (
                <BulkInviteModal
                    searchId={searchId}
                    onClose={() => setShowBulkModal(false)}
                    onDone={() => loadSearch(searchId)}
                />
            )}
            {copyPreset && (
                <PresetCopyModal
                    preset={copyPreset}
                    sourceSearchId={searchId}
                    onClose={() => setCopyPreset(null)}
                />
            )}
        </div>
    );
}

function _restoredLabel(iso) {
    // Compact "Restored just now / 3h ago / 2d ago" label for the amber
    // reappearance pill. Returns null when the timestamp is missing or
    // beyond the 7-day freshness window (server already filters, but
    // stay defensive on the client too).
    if (!iso) return null;
    const t = Date.parse(iso);
    if (isNaN(t)) return null;
    const mins = Math.floor((Date.now() - t) / 60000);
    if (mins < 0 || mins >= 60 * 24 * 7) return null;
    if (mins < 60) return "Restored just now";
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Restored ${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `Restored ${days}d ago`;
}

function CandidateCard({ c, onOpen }) {
    const scoreColor = c.match_score >= 70 ? "text-emerald-600" : c.match_score >= 40 ? "text-amber-600" : "text-slate-400";
    const restoredLabel = _restoredLabel(c.restored_at);
    return (
        <button
            onClick={onOpen}
            data-testid={`candidate-card-${c.candidate_id}`}
            className="text-left card-soft p-4 hover:border-violet-500/40 transition"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 flex items-start gap-3">
                    {/* Avatar rollout iter 93 — Recruit search card.
                        Admin surface, so we DO show real photos when
                        available; Avatar falls back to a deterministic
                        initials tile if not, so cards never look broken
                        while talent procrastinates on uploading. */}
                    <Avatar
                        url={c.profile_photo_url || null}
                        name={c.name || ""}
                        email={c.email || ""}
                        userId={c.candidate_id || ""}
                        size={40}
                        testId={`candidate-avatar-${c.candidate_id}`}
                    />
                    <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading font-medium truncate">{c.name || "Unknown"}</span>
                        {c.source === "simonara" ? (
                            <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-400 text-[10px]">
                                <ShieldCheck size={9} className="mr-1" /> Simonara
                            </Badge>
                        ) : (
                            <Badge variant="outline" className="border-sky-500/40 text-sky-700 dark:text-sky-400 text-[10px]">
                                <Globe2 size={9} className="mr-1" /> Apollo
                            </Badge>
                        )}
                        {c.has_passport && c.source === "apollo" && (
                            <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 text-[10px]">
                                <Star size={9} className="mr-1" /> Also in Simonara
                            </Badge>
                        )}
                        {c.id_verified && (
                            <Badge data-testid={`candidate-verified-${c.candidate_id}`} className="bg-emerald-500 hover:bg-emerald-500 text-white text-[10px]">
                                <ShieldCheck size={9} className="mr-1" /> Verified
                            </Badge>
                        )}
                        {restoredLabel && (
                            <Badge
                                data-testid={`candidate-restored-${c.candidate_id}`}
                                className="bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/40 hover:bg-amber-500/15 text-[10px]"
                                title={`Talent restored their passport at ${new Date(c.restored_at).toLocaleString()}`}
                            >
                                <RotateCcw size={9} className="mr-1" /> {restoredLabel}
                            </Badge>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {c.title || "—"}{c.current_company ? ` @ ${c.current_company}` : ""}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-2">
                        {c.location ? (
                            <span data-testid={`candidate-location-${c.candidate_id}`}>
                                <MapPin size={10} className="inline -mt-0.5" /> {c.location}
                            </span>
                        ) : c.source === "apollo" ? (
                            <span
                                data-testid={`candidate-location-unknown-${c.candidate_id}`}
                                className="italic opacity-70"
                                title="Apollo returned no location data for this profile"
                            >
                                <MapPin size={10} className="inline -mt-0.5" /> Location unknown
                            </span>
                        ) : null}
                        {c.years_experience && <span>· {c.years_experience} yrs</span>}
                        {c.availability ? (
                            <span>· {c.availability}</span>
                        ) : c.source === "apollo" ? (
                            <span
                                data-testid={`candidate-availability-apollo-${c.candidate_id}`}
                                className="italic opacity-70"
                                title="Apollo doesn't track candidate availability — invite the candidate to build a Simonara Passport to see this"
                            >
                                · Availability unknown
                            </span>
                        ) : null}
                        {c.source === "apollo" && !c.email_available && (
                            <span
                                data-testid={`candidate-email-hidden-${c.candidate_id}`}
                                className="italic opacity-70"
                                title="Apollo hides emails behind their paid enrichment tier — invite to unlock"
                            >
                                · Email hidden
                            </span>
                        )}
                    </div>
                </div>
                </div>
                <div className="text-right shrink-0">
                    <div className={`font-heading font-bold mono-score text-2xl ${scoreColor}`}>{c.match_score}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">match</div>
                    {c.trust_score > 0 && (
                        <div className="text-[10px] text-muted-foreground mt-1">Verified {c.trust_score}</div>
                    )}
                </div>
            </div>
            {c.matched_skills?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {c.matched_skills.slice(0, 6).map((s, i) => (
                        <span key={i} className="text-[10px] bg-violet-500/10 text-violet-700 dark:text-violet-300 rounded px-1.5 py-0.5">{s}</span>
                    ))}
                    {c.matched_skills.length > 6 && <span className="text-[10px] text-muted-foreground">+{c.matched_skills.length - 6}</span>}
                </div>
            )}
        </button>
    );
}
